import React, { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import App from "./App.jsx";
import { getSession, devLogin } from "./api.js";
import SuiteSwitcher from "./SuiteSwitcher.jsx";
import { showPrivacyLink } from "./privacyLink.js";

const NAVY = "#1b2a4a";
const RED = "#b22234";
const MUTED = "#56607a";
const BODY_FONT = "'Public Sans', system-ui, -apple-system, 'Segoe UI', sans-serif";
const DISPLAY_FONT = "'Merriweather', Georgia, serif";

const card = { maxWidth: 440, margin: "60px auto", background: "#fff", border: "1px solid #d9dde7", borderTop: `4px solid ${RED}`, borderRadius: 10, padding: "26px 28px", fontFamily: BODY_FONT, color: "#18213a" };
const h1 = { fontFamily: DISPLAY_FONT, fontSize: 21, margin: "0 0 8px", color: NAVY };
const btn = { background: NAVY, color: "#fff", border: "none", borderRadius: 8, padding: "10px 16px", fontWeight: 700, fontFamily: BODY_FONT, fontSize: 14, cursor: "pointer", textDecoration: "none", display: "inline-block" };
const input = { width: "100%", boxSizing: "border-box", padding: "9px 10px", border: "1px solid #d9dde7", borderRadius: 7, fontFamily: BODY_FONT, fontSize: 14, marginBottom: 10 };

// 2.0.3: the suite's 9-dot app switcher at the top right of these cards too, so someone who isn't
// signed in (or whose office doesn't have the tracker) can still reach the suite's other tools;
// signed out, it shows the suite's sign-up note.
function CardHeading({ children, suite, office }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
      <h1 style={h1}>{children}</h1>
      <div style={{ margin: "-10px -12px 0 0" }}><SuiteSwitcher suite={suite} office={office} color={NAVY} accent={NAVY} /></div>
    </div>
  );
}

// Local development only: the Worker offers this when DEV_LOGIN=1 and no real suite sign-in
// secret is configured. It stands in for the suite account service until that exists.
function DevLogin({ onDone }) {
  const [form, setForm] = useState({ name: "Pat Sample", email: "pat@anytown.example", orgId: "anytown", orgName: "Anytown, USA", role: "owner", demoDays: "", confirmed: true });
  const [err, setErr] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  async function submit(e) {
    e.preventDefault();
    try {
      await devLogin({ ...form, demoDays: form.demoDays === "" ? undefined : Number(form.demoDays), confirmed: !!form.confirmed });
      onDone();
    } catch (x) {
      setErr(x.message);
    }
  }
  return (
    <form style={card} onSubmit={submit}>
      <h1 style={h1}>Test sign-in</h1>
      <p style={{ color: MUTED, fontSize: 13.5, marginTop: 0 }}>
        This stands in for the Election Admin Suite sign-in, which isn't built yet. It only appears on a local test copy.
      </p>
      <input style={input} value={form.name} onChange={set("name")} placeholder="Your name" aria-label="Your name" />
      <input style={input} value={form.email} onChange={set("email")} placeholder="Email" aria-label="Email" />
      <input style={input} value={form.orgId} onChange={set("orgId")} placeholder="Office id (e.g. anytown)" aria-label="Office id" />
      <input style={input} value={form.orgName} onChange={set("orgName")} placeholder="Office name" aria-label="Office name" />
      <select style={input} value={form.role} onChange={set("role")} aria-label="Role">
        <option value="owner">Owner</option>
        <option value="admin">Admin</option>
        <option value="user">User</option>
      </select>
      <input style={input} value={form.demoDays} onChange={set("demoDays")} placeholder="Demo days left (blank = full access)" aria-label="Demo days left" />
      {/* 2.1.3: untick to try what someone without a confirmed sign-in sees (restore, election delete). */}
      <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13.5, marginBottom: 10 }}>
        <input type="checkbox" checked={form.confirmed} onChange={(e) => setForm({ ...form, confirmed: e.target.checked })} />
        Confirmed sign-in (password and code)
      </label>
      {err && <div style={{ color: RED, fontSize: 13, marginBottom: 10 }}>{err}</div>}
      <button style={btn} type="submit">Sign in</button>
    </form>
  );
}

const subtleBtn = { ...btn, background: "#fff", color: NAVY, border: "1px solid #d9dde7" };

// 2.1.0: sign in with the same account as Poll Worker Management. The office is the first part of
// its Poll Worker Management address (greenwich for greenwich.electionadminsuite.com), remembered
// on this computer. The Worker's /signin/pwm/start sends the browser there; once signed in, Poll
// Worker Management sends it back here signed in (worker/src/pwm.js).
function PwmSignIn() {
  const [office, setOffice] = useState(() => { try { return localStorage.getItem("ett-pwm-office") || ""; } catch { return ""; } });
  function go(e) {
    e.preventDefault();
    const id = office.trim().toLowerCase();
    if (!id) return;
    try { localStorage.setItem("ett-pwm-office", id); } catch {}
    location.href = `/signin/pwm/start?office=${encodeURIComponent(id)}`;
  }
  return (
    <form onSubmit={go}>
      <p style={{ color: MUTED, marginTop: 0 }}>Sign in with the same account you use for Poll Worker Management. You'll go to your office's Poll Worker Management page, sign in there if it asks, and come straight back.</p>
      <label htmlFor="pwm-office" style={{ display: "block", fontSize: 13, fontWeight: 700, color: NAVY, marginBottom: 4 }}>Your office</label>
      <input id="pwm-office" style={input} value={office} onChange={(e) => setOffice(e.target.value)} placeholder="greenwich" autoCapitalize="none" autoCorrect="off" spellCheck={false} />
      <p style={{ color: MUTED, fontSize: 12.5, margin: "-4px 0 12px" }}>The first part of your Poll Worker Management address: for <b>greenwich</b>.electionadminsuite.com, type <b>greenwich</b>.</p>
      <button style={btn} type="submit" disabled={!office.trim()}>Sign in with Poll Worker Management</button>
    </form>
  );
}

function Root() {
  const [state, setState] = useState({ phase: "loading" });

  async function refresh() {
    try {
      const session = await getSession();
      showPrivacyLink(session.suiteOrigin);
      setState({ phase: "ready", session });
    } catch (e) {
      showPrivacyLink(e.body && e.body.suiteOrigin);
      if (e.status === 401) setState({ phase: "signed-out", ...e.body });
      else if (e.status === 403) setState({ phase: "forbidden", message: e.message, ...e.body });
      else setState({ phase: "error", message: e.message });
    }
  }
  useEffect(() => { refresh(); }, []);

  if (state.phase === "loading") return <div style={{ ...card, textAlign: "center", color: MUTED }}>Loading…</div>;
  if (state.phase === "error") {
    return (
      <div style={card}>
        <h1 style={h1}>Couldn't reach the task tracker</h1>
        <p style={{ color: MUTED }}>{state.message}</p>
        <button style={btn} onClick={refresh}>Try again</button>
      </div>
    );
  }
  if (state.phase === "forbidden") {
    return (
      <div style={card}>
        <CardHeading suite={state.suiteOrigin}>Election Task Tracker</CardHeading>
        <p style={{ color: MUTED }}>{state.message}</p>
        {state.toolsUrl && <a style={btn} href={state.toolsUrl}>Go to your office's tools</a>}
      </div>
    );
  }
  if (state.phase === "signed-out") {
    if (state.devLogin) return <DevLogin onDone={refresh} />;
    return (
      <div style={card}>
        <CardHeading suite={state.suiteOrigin}>Election Task Tracker</CardHeading>
        {state.error && state.error !== "Please sign in" && <p style={{ color: RED, fontSize: 14 }}>{state.error}</p>}
        {state.pwmSignIn && <PwmSignIn />}
        {state.signInUrl && (
          <>
            {state.pwmSignIn && <p style={{ color: MUTED, fontSize: 13.5, margin: "22px 0 8px", borderTop: "1px solid #d9dde7", paddingTop: 16 }}>Or use an Election Admin Suite account:</p>}
            {!state.pwmSignIn && <p style={{ color: MUTED }}>Sign in with your Election Admin Suite account to see your office's tasks.</p>}
            <a style={state.pwmSignIn ? subtleBtn : btn} href={`${state.signInUrl}?next=${encodeURIComponent(location.href)}`}>Sign in with the Election Admin Suite</a>
          </>
        )}
      </div>
    );
  }
  const { session } = state;
  if (session.access === "none") {
    return (
      <div style={card}>
        <CardHeading suite={session.suiteOrigin} office={session.org.id}>Your office doesn't have the task tracker yet</CardHeading>
        <p style={{ color: MUTED }}>An owner or admin can start a free 30-day demo, or contact us to buy it, from {session.org.name}'s account page.</p>
        {session.contactUrl && <a style={btn} href={session.contactUrl}>Go to your account page</a>}
      </div>
    );
  }
  return <App session={session} onSignedOut={refresh} />;
}

createRoot(document.getElementById("root")).render(<Root />);
